// GI31 화면 행 모델과 저장 전 검증. 1.0 GI31.js 의 onHandleSave·onHandleSend 검증을 그대로 옮긴 것이다.
import {
  EDITED_FLAG,
  EditedFlag,
  GeneralOutboundFilter,
  GeneralOutboundItem,
  SaveGeneralOutboundItem,
} from '@/services/outbound/generalOutbound/types'
import {
  CONFIRMABLE_ORDER_TYPES,
  CONFIRMABLE_TRANSPORT_TYPES,
  GLOBAL_REQUIRED_COLUMNS,
  GLOBAL_SHIPPER_CODE,
  MAX_CONFIRM_SIZE,
  MAX_SAVE_SIZE,
  MESSAGES,
  REQUIRED_COLUMNS,
} from '@/pages/outbound/generalOutbound/components/consts'

/** 표가 다루는 행 — 조회 결과에 행키와 편집 플래그를 얹는다. */
export type GeneralOutboundRow = GeneralOutboundItem & {
  _rowKey: string
  /** 'I' = 엑셀 업로드로 들어온 신규 행 · 'D' = [삭제] 가 저장 요청에 실을 때만 붙인다. 그 외는 조회된 기존 행. */
  _edited?: EditedFlag
}

/** 엑셀 업로드 행이 채우지 않은 자리를 메우는 기본값. 조회 응답과 키 구성을 맞춰 표가 같은 컬럼을 그린다. */
const EMPTY_ITEM: Omit<GeneralOutboundItem, 'ID' | 'SPR_NM'> = {
  ORD_TYPE: '',
  TRS_TYPE: '',
  UID: '',
  BARCODE: '',
  SKU_ID: null,
  GDS_CD: '',
  GDS_NM: '',
  BRD_NM: '',
  OPT_CD: null,
  GDS_OPT: '',
  STL_NO: '',
  GIFT: '',
  ORD_NO: '',
  ORD_OPT_NO: '',
  ORD_QTY: 0,
  ORD_NM: '',
  TRS_NM: '',
  TRS_ZIP: '',
  TRS_ADDR: '',
  TRS_MOBILE: '',
  TRS_PHONE: '',
  TRS_MSG: '',
  TRS_PASS: '',
  PROC_NM: '',
  PLN_DATE: '',
  ETC1: '',
  ETC2: '',
  INS_DATE: '',
  INS_BY: '',
  UPD_DATE: '',
  UPD_BY: '',
}

export const toRows = (items: GeneralOutboundItem[]): GeneralOutboundRow[] =>
  items.map((item, i) => ({ ...item, _rowKey: `r-${item.ID ?? i}-${i}` }))

/**
 * 엑셀 업로드 행 → 신규 행. ID 가 없어 저장 시 INSERT 로 처리된다.
 *
 * 화주는 파일 값이 아니라 조회 조건의 화주로 고정한다. BE 가 요청 화주와 다른 행이 섞이면
 * 저장을 통째로 거부하고(`validateShipperCode`), 저장은 어차피 조회 화주로 나간다.
 * 파일 값을 표에만 남기면 보이는 것과 저장되는 것이 달라진다.
 */
export const toNewRow = (
  imported: Record<string, string | number>,
  shipperCode: string,
  seq: number,
): GeneralOutboundRow => ({
  ...EMPTY_ITEM,
  ID: 0,
  ...(imported as Partial<GeneralOutboundItem>),
  SPR_NM: shipperCode,
  _rowKey: `imp-${seq}`,
  _edited: EDITED_FLAG.insert,
})

/**
 * 주문번호 길이 검증에 쓰는 바이트 수.
 *
 * 1.0 `ValidationUtil.getByteLengthOfStringValidate` 와 같은 계산이다. 원본은 시프트 연산으로
 * 경계를 나눴는데(`code >> 11`, `code >> 7`) 같은 경계를 그대로 수로 적었다 — 2048 이상이면 3바이트,
 * 128 이상이면 2바이트, 나머지 1바이트.
 */
export const byteLength = (value: unknown): number => {
  // 코드포인트 단위로 센다. charCodeAt 로 세면 이모지 같은 BMP 밖 문자가 서로게이트 두 조각으로
  // 잡혀 3+3=6 바이트가 된다(실제 UTF-8 은 4).
  return [...String(value ?? '')].reduce((bytes, char) => {
    const code = char.codePointAt(0) ?? 0
    if (code >= 65536) return bytes + 4
    if (code >= 2048) return bytes + 3
    if (code >= 128) return bytes + 2
    return bytes + 1
  }, 0)
}

/** 주문번호 최대 바이트 — 1.0 GI31.js 가 25 바이트 초과를 붉게 표시하고 저장을 막았다. */
export const ORDER_NO_MAX_BYTES = 25

// 1.0 ValidationUtil 의 정규식 그대로다. `*` 를 허용하는 이유는 마스킹된 값이 그대로 흘러오기
// 때문이다(dev 실측: 핸드폰 `010-****-1234`, 우편번호 `06***`). 빼면 기존 데이터를 다시 저장할 때
// 막힌다. 안내 문구도 1.0 과 같은 문장을 쓴다.
const PHONE_PATTERN = /^[0-9\s\-+*]*$/
const ZIP_PATTERN = /^[0-9\-*]*$/

const isPhoneValid = (v: unknown) => !v || PHONE_PATTERN.test(String(v).trim())
const isZipValid = (v: unknown) => !v || ZIP_PATTERN.test(String(v).trim())

/** 값이 비었는지 — 편집 셀을 지우면 빈 문자열이 되고, 수량은 0 이하가 미입력과 같다. */
const isBlank = (row: GeneralOutboundRow, key: string): boolean => {
  const value = (row as unknown as Record<string, unknown>)[key]
  // 수량은 Number('') 가 0 이라 빈 값이 그냥 통과한다. 0 도 미입력으로 본다(BE quantity <= 0).
  if (key === 'ORD_QTY') return !(Number(value) > 0)
  if (key === 'OPT_CD') return value == null || String(value).trim() === ''
  return String(value ?? '').trim() === ''
}

/**
 * 필수값 누락을 찾는다. 목록은 [REQUIRED_COLUMNS] 한 곳에서 오고 표의 별표도 같은 곳에서 나온다.
 * 1.0 `CommonUtil.onSaveData`(required 컬럼) 기준이다 — moms-be `missingFields` 보다 넓어(연락처 둘 다·주문/배송유형) 서버 왕복 전에 잡힌다.
 */
const findMissingField = (rows: GeneralOutboundRow[], shipperCode: string): string | null => {
  const required = shipperCode === GLOBAL_SHIPPER_CODE ? [...REQUIRED_COLUMNS, ...GLOBAL_REQUIRED_COLUMNS] : REQUIRED_COLUMNS

  for (let i = 0; i < rows.length; i += 1) {
    const missing = required.find((column) => isBlank(rows[i], column.key))
    if (missing) return `${i + 1}번째 행의 ${missing.label}이(가) 비어 있습니다.`
  }
  return null
}

/**
 * 저장 전 검증. 걸리는 게 있으면 안내 문구를 돌려주고, 없으면 null 이다.
 *
 * 1.0 GI31.js `onHandleSave` 의 형식 검증(주문번호 바이트·전화·우편번호)에 더해
 * BE 가 요구하는 필수값까지 여기서 본다. 형식만 보면 빈 칸이 그대로 나가 BE 400 으로 튕긴다.
 */
export const validateBeforeSave = (rows: GeneralOutboundRow[], shipperCode = ''): string | null => {
  if (!rows.length) return MESSAGES.selectRequired
  if (rows.length > MAX_SAVE_SIZE) return MESSAGES.saveSizeExceeded

  const targets = rows.filter((r) => r._edited !== EDITED_FLAG.delete)
  if (targets.some((r) => byteLength(r.ORD_NO) > ORDER_NO_MAX_BYTES)) return MESSAGES.orderNoTooLong
  if (targets.some((r) => !isPhoneValid(r.TRS_MOBILE) || !isPhoneValid(r.TRS_PHONE))) return MESSAGES.phoneInvalid
  if (targets.some((r) => !isZipValid(r.TRS_ZIP))) return MESSAGES.zipInvalid
  return findMissingField(targets, shipperCode)
}

/**
 * 확정 전 검증.
 *
 * BE `ValidateGeneralOutboundService.validateConfirm` 이 주문유형·배송유형을 필수로 보고 일반출고가
 * 허용하는 값인지까지 확인한다. 같은 판정을 화면에서 먼저 해 서버 왕복 없이 안내한다(1.0 도 같았다).
 */
export const validateBeforeConfirm = (rows: GeneralOutboundRow[], filter: GeneralOutboundFilter): string | null => {
  if (!rows.length) return MESSAGES.selectRequired
  if (rows.length > MAX_CONFIRM_SIZE) return MESSAGES.confirmSizeExceeded
  if (!filter.ORD_TYPE) return MESSAGES.orderTypeRequired
  if (!filter.TRS_TYPE) return MESSAGES.transportTypeRequired
  if (!CONFIRMABLE_ORDER_TYPES.includes(filter.ORD_TYPE)) return MESSAGES.orderTypeNotConfirmable
  if (!CONFIRMABLE_TRANSPORT_TYPES.includes(filter.TRS_TYPE)) return MESSAGES.transportTypeNotConfirmable
  // 엑셀 업로드로 들어온 미저장 행은 ID 가 없어 확정 대상이 될 수 없다.
  if (rows.some((r) => !r.ID)) return MESSAGES.confirmNotSaved
  return null
}

/**
 * 옵션코드를 숫자로 정규화한다.
 *
 * 타입은 `number | null` 이지만 편집 셀을 지우면 런타임에 빈 문자열이 들어온다. 그대로 Number() 하면
 * 0 이 되어 실제 옵션코드 0 과 구분이 안 된다. 숫자로 읽히지 않으면 미입력으로 본다.
 */
export const toOptionCode = (value: unknown): number | null => {
  const text = String(value ?? '').trim()
  if (text === '') return null
  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : null
}

const nullable = (v: unknown) => {
  const s = String(v ?? '').trim()
  return s === '' ? null : s
}

/**
 * 출고예정일자를 저장 형식(yyyyMMdd)으로 맞춘다.
 *
 * BE 는 `LocalDate.parse(it, "yyyyMMdd")` 로만 읽고 실패하면 조용히 오늘로 대체한다
 * (`GeneralOutboundPayload.parsePlanDate`). 그런데 조회 응답(moms-be#4433)과 표의 datepicker 셀은
 * `2026-07-14` 형태다. 받은 값을 그대로 되돌려 보내면 파싱이 깨져 사용자가 지정한 날짜가 오늘로 바뀐다.
 * 구분자만 걷어내 같은 날짜가 그대로 저장되게 한다. 날짜로 보이지 않는 값은 손대지 않는다.
 */
export const toSavePlanDate = (value: unknown): string | null => {
  const s = String(value ?? '').trim()
  if (s === '') return null
  const digits = s.replace(/[^0-9]/g, '')
  return digits.length === 8 ? digits : s
}

/** 화면 행 → 저장 요청 행. 신규는 INSERT, 삭제 표시(`_edited` 'D', [삭제] 버튼)는 DELETE, 그 밖의 조회된 행은 UPDATE 로 보낸다. */
export const toSaveItem = (row: GeneralOutboundRow, shipperCode: string, shipperName: string): SaveGeneralOutboundItem => ({
  ID: row._edited === EDITED_FLAG.insert ? null : row.ID,
  SPR_NM: shipperCode,
  UID: nullable(row.UID),
  BARCODE: String(row.BARCODE ?? '').trim(),
  GDS_CD: nullable(row.GDS_CD),
  GDS_NM: nullable(row.GDS_NM),
  BRD_NM: nullable(row.BRD_NM),
  OPT_CD: toOptionCode(row.OPT_CD),
  GDS_OPT: nullable(row.GDS_OPT),
  STL_NO: nullable(row.STL_NO),
  GIFT: nullable(row.GIFT),
  ORD_NO: String(row.ORD_NO ?? '').trim(),
  ORD_OPT_NO: String(row.ORD_OPT_NO ?? '').trim(),
  ORD_QTY: Number(row.ORD_QTY) || 0,
  ORD_NM: String(row.ORD_NM ?? '').trim(),
  TRS_ADDR: String(row.TRS_ADDR ?? '').trim(),
  TRS_ZIP: String(row.TRS_ZIP ?? '').trim(),
  TRS_MOBILE: String(row.TRS_MOBILE ?? '').trim(),
  TRS_PHONE: String(row.TRS_PHONE ?? '').trim(),
  TRS_NM: String(row.TRS_NM ?? '').trim(),
  TRS_MSG: nullable(row.TRS_MSG),
  TRS_PASS: nullable(row.TRS_PASS),
  PROC_NM: nullable(row.PROC_NM),
  TRS_TYPE: nullable(row.TRS_TYPE),
  ORD_TYPE: nullable(row.ORD_TYPE),
  PLN_DATE: toSavePlanDate(row.PLN_DATE),
  ETC1: nullable(row.ETC1),
  ETC2: nullable(row.ETC2),
  EDITTED: row._edited ?? EDITED_FLAG.update,
  // 1.0 ShipperUtils.reformatSaveDataWithShipperName 가 행마다 붙이던 값. BE 가 화주 전략 분기에 쓴다.
  shipper_code: shipperCode,
  shipper_name: shipperName || shipperCode,
})
